import { memo, Suspense, lazy } from 'react';
import React, { ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/components/auth/AuthProvider';
import { ThemeProvider } from '@/components/theme/ThemeProvider';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/footer';
import { AuthCallback } from '@/pages/auth/callback';
import { useAuth } from '@/components/auth/AuthProvider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClientProvider } from '@tanstack/react-query';
import { QueryClient } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { HeadTags } from '@/components/layout/HeadTags';
import { Toaster } from '@/components/ui/toaster';
import { LoadingSpinner } from '@/components/ui/loading';
import { ScrollToTop } from '@/components/ui';
import { MotionProvider } from '@/components/ui/motion-provider';

// Lazy load components with robust error handling
const Home = lazy(() => import('@/pages/Home'));

const SearchFallback = () => {
  console.error('Search component failed to load');
  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-red-500">Failed to load Search page. Please refresh or contact support.</p>
    </div>
  );
};

const Search = lazy(() => 
  import('@/pages/Search').then(module => {
    if (!module.default) {
      console.error('Search module does not have a default export');
      throw new Error('Search module does not have a default export');
    }
    return module;
  }).catch(error => {
    console.error('Failed to load Search module:', error);
    return { default: SearchFallback };
  })
);

const SavedProfiles = lazy(() => import('@/components/saved/SavedProfiles').then(module => {
  if (!module.default) {
    throw new Error('SavedProfiles module does not have a default export');
  }
  return { default: module.default };
}).catch(error => {
  console.error('Failed to load SavedProfiles module:', error);
  return { default: () => <div>Failed to load Saved Profiles</div> };
}));

const SavedSearches = lazy(() => import('@/pages/SavedSearches').then(module => {
  if (!module.default) {
    throw new Error('SavedSearches module does not have a default export');
  }
  return { default: module.default };
}).catch(error => {
  console.error('Failed to load SavedSearches module:', error);
  return { default: () => <div>Failed to load Saved Searches</div> };
}));

const Settings = lazy(() => import('@/pages/Settings'));
const Docs = lazy(() => import('@/pages/Docs'));
const ShareableProfileList = lazy(() => import('@/components/shared/ShareableProfileList'));
const Inbox = lazy(() => import('@/pages/Inbox'));
const GmailCallback = lazy(() => import('@/pages/GmailCallback'));

import '@/styles/globals.css';
import '@/styles/patterns.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 1
    },
  },
});

// RequireAuth component to wrap routes that require authentication
const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// ErrorBoundary component
class ErrorBoundary extends React.Component<{ children: ReactNode }, { hasError: boolean; error?: Error }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    console.error('Uncaught error:', error);
    console.error('Error Info:', errorInfo);
    // Optional: Send error to monitoring service like Sentry
    // Sentry.captureException(error);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div className="error-boundary">
          <h1>Something went wrong.</h1>
          <p>Error Details: {this.state.error?.message}</p>
          <button onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrap component with Suspense boundary and ErrorBoundary
const SuspenseWrapper = ({ children }: { children: ReactNode }) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingSpinner />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

// Memoize AppContent to prevent unnecessary re-renders
const AppContent = memo(() => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="text-center flex flex-col items-center">
          <LoadingSpinner className="w-12 h-12 text-primary" />
          <p className="font-inter text-sm text-muted-foreground/80 mt-4 animate-pulse">
            Preparing your DevFinder experience...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex-1 relative py-4 sm:py-6 md:py-8">
        <div className="mx-auto max-w-7xl px-3 sm:px-4 md:px-6 lg:px-8 h-full">
          <div className="flex flex-col h-full">
            <div className="rounded-xl sm:rounded-2xl bg-background/50 backdrop-blur-sm shadow-sm flex-1">
              <div className="p-4 sm:p-6 md:p-8 lg:p-10 h-full">
                <Routes>
                  <Route path="/" element={user ? <Navigate to="/home" replace /> : <SuspenseWrapper><Home /></SuspenseWrapper>} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/gmail-callback" element={<SuspenseWrapper><GmailCallback /></SuspenseWrapper>} />
                  <Route path="/home" element={<RequireAuth><SuspenseWrapper><Home /></SuspenseWrapper></RequireAuth>} />
                  <Route path="/search" element={<RequireAuth><SuspenseWrapper><Search /></SuspenseWrapper></RequireAuth>} />
                  <Route path="/docs" element={<RequireAuth><SuspenseWrapper><Docs /></SuspenseWrapper></RequireAuth>} />
                  <Route path="/saved-profiles" element={<RequireAuth><SuspenseWrapper><SavedProfiles /></SuspenseWrapper></RequireAuth>} />
                  <Route path="/saved-searches" element={<RequireAuth><SuspenseWrapper><SavedSearches /></SuspenseWrapper></RequireAuth>} />
                  <Route path="/settings" element={<RequireAuth><SuspenseWrapper><Settings /></SuspenseWrapper></RequireAuth>} />
                  <Route path="/shared/:shareId" element={<SuspenseWrapper><ShareableProfileList /></SuspenseWrapper>} />
                  <Route path="/gmail" element={<RequireAuth><SuspenseWrapper><Inbox /></SuspenseWrapper></RequireAuth>} />
                </Routes>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
      <Toaster />
      <ScrollToTop />
    </div>
  );
});

AppContent.displayName = 'AppContent';

import { BrowserRouter } from 'react-router-dom';

export default function App() {
  return (
    <HelmetProvider>
      <HeadTags />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <MotionProvider>
              <TooltipProvider>
                <BrowserRouter>
                  <Suspense 
                    fallback={
                      <div className="flex h-screen items-center justify-center">
                        <div className="h-32 w-32 animate-spin rounded-full border-b-2 border-gray-900" />
                      </div>
                    }
                  >
                    <AppContent />
                  </Suspense>
                </BrowserRouter>
              </TooltipProvider>
            </MotionProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
